/* eslint-disable max-len */
import React, { useEffect, useRef } from 'react'
import Button from '@components/common/Button/Button.component'
import LoadingIndicator from '@components/common/LoadingIndicator/LoadingIndicator.component'
import NotesMainContent from '@components/pages/Notes/NotesMainContent.component'
import NoteEditor from '@components/pages/Notes/NoteEditor'
import { Patient } from '@dataTypes/PatientInfo.types'
import { NoteContextType } from '@dataTypes/note.type'
import type { PreviousPageState } from './notesFeature.types'
import type { UseNotesEditorFeatureResult } from './useNotesEditorFeature'

export type NotesFeatureMainColumnProps = {
  feature: UseNotesEditorFeatureResult
  selectedPatient: Patient | null
  selectedPatientName: string
  patientPreviousPageState: PreviousPageState | null
  originContextId: string | null
  originContactId: string | null
  originContextType: NoteContextType | null
  originContextName: string | null
  originOpenFirstNote: boolean
  /** Pre-filled title for the new-note editor (e.g. from X-ray Viewer "Note for xray_file_445"). */
  originInitialTitle?: string | null
  notesLoading: boolean
  notesError: string | undefined
  /** Include patient sidebar in layout (Notes page); overlay omits and forces main column full width */
  showPatientSideSlot: boolean
  /**
   * When omitted, derived from patientPreviousPageState (route flows). Set for overlay tabs
   * without router previousPage (e.g. Charting).
   */
  openContextNotesByDefault?: boolean
  /** When true, suppress NotesMainContent table fallback and keep editor-only flow. */
  hideNotesTable?: boolean
  /** When true (side panel list overlay), render overlay close in the table header row only. */
  overlayListCloseInTableHeader?: boolean
  /** Label for the in-header overlay back control. */
  overlayListCloseLabel?: string
  /** Claim overlay: when true, disable new note and edit in the editor. */
  claimContextNotesReadOnly?: boolean
  /** Re-open the tray-owned select-patient modal from the empty state. */
  onSelectPatient?: () => void
}

const NotesFeatureMainColumn: React.FC<NotesFeatureMainColumnProps> = ({
  feature,
  selectedPatient,
  selectedPatientName,
  patientPreviousPageState,
  originContextId,
  originContactId,
  originContextType,
  originContextName,
  originOpenFirstNote,
  originInitialTitle,
  notesLoading,
  notesError,
  showPatientSideSlot,
  openContextNotesByDefault: openContextNotesByDefaultProp,
  hideNotesTable = false,
  overlayListCloseInTableHeader = false,
  overlayListCloseLabel = 'Back',
  claimContextNotesReadOnly = false,
  onSelectPatient,
}) => {
  const openContextNotesInEditor =
    openContextNotesByDefaultProp ?? Boolean(patientPreviousPageState)

  const {
    isSidebarOpen,
    toggleSidebar,
    isAddingNote,
    isEditingNote,
    editingNote,
    isViewingNote,
    viewingNote,
    noteSearchQuery,
    noteFilters,
    showContextViewPrereqLoader,
    showContextViewResolvedOverlay,
    handleNoteSearchChange,
    handleNoteFilterChange,
    handleNoteResetFilters,
    handleAddNote,
    handleBackToNotes,
    handleSaveNote,
    handleViewNote,
    handleBackFromView,
    handleEditNote,
    handleDeleteNote,
    handleDownloadNote,
    handleDeleteNoteFromEditor,
    handleBackFromEdit,
    showTranscribedAudio,
    handleViewTranscription,
    handleHideTranscription,
    handleTranscriptSegmentsChange,
    onNavigationGuardChange,
    onRegisterSaveAndLeave,
    onInitialContextNoteResolved,
    contextInitialNoteResolved,
    newNoteSeedContext,
    backButtonLabel,
    addNotePatientName,
    selectedPatientNotes,
    requestOverlayClose,
  } = feature

  const inEditor =
    isAddingNote || isEditingNote || (isViewingNote && viewingNote != null)
  const didAutoRecoverEditorRef = useRef<boolean>(false)

  const shouldShowGlobalOverlayLoader =
    showContextViewPrereqLoader ||
    showContextViewResolvedOverlay ||
    (hideNotesTable && !notesError && !inEditor) ||
    (!showPatientSideSlot && notesLoading)

  useEffect(() => {
    if (!hideNotesTable || notesError || notesLoading) {
      didAutoRecoverEditorRef.current = false
      return
    }
    if (originOpenFirstNote) {
      // View-first flows (patient or contact/call context): do not force Add mode.
      // Let the first-note bootstrap in useNotesEditorFeature open view mode.
      didAutoRecoverEditorRef.current = false
      return
    }
    if (inEditor) {
      didAutoRecoverEditorRef.current = false
      return
    }
    if (didAutoRecoverEditorRef.current) {
      return
    }
    didAutoRecoverEditorRef.current = true
    handleAddNote()
  }, [
    hideNotesTable,
    notesError,
    notesLoading,
    originOpenFirstNote,
    inEditor,
    handleAddNote,
  ])

  const mainContentClassName = [
    'notes-main-content',
    !isSidebarOpen ||
    originContextId ||
    originContactId ||
    isViewingNote ||
    !showPatientSideSlot
      ? 'sidebar-hidden'
      : '',
    selectedPatientNotes.length === 0 &&
    !originContextId &&
    !originContactId &&
    showPatientSideSlot
      ? 'no-notes'
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={mainContentClassName}>
      <LoadingIndicator
        isLoading={shouldShowGlobalOverlayLoader}
        container={document.body}
      />
      {isAddingNote ? (
        showContextViewPrereqLoader ? null : (
          <div
            className={[
              'notes-add-note-editor-mount',
              showContextViewResolvedOverlay
                ? 'notes-context-view-bootstrap-wrap'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <NoteEditor
              key="add-new"
              contextId={originContextId ?? undefined}
              contactId={originContactId ?? undefined}
              contextType={originContextType ?? NoteContextType.PATIENT}
              contextName={originContextName ?? undefined}
              patientId={selectedPatient?.patient_id}
              openContextNotesByDefault={openContextNotesInEditor}
              selectFirstNote={
                originOpenFirstNote && !contextInitialNoteResolved
              }
              initialTitle={originInitialTitle ?? undefined}
              patientName={addNotePatientName}
              backButtonLabel={backButtonLabel}
              onBack={handleBackToNotes}
              onSave={handleSaveNote}
              onEditNote={handleEditNote}
              onToggleSidebar={toggleSidebar}
              onViewTranscription={handleViewTranscription}
              onHideTranscription={handleHideTranscription}
              showTranscription={showTranscribedAudio}
              onTranscriptSegmentsChange={handleTranscriptSegmentsChange}
              onNavigationGuardChange={onNavigationGuardChange}
              onRegisterSaveAndLeave={onRegisterSaveAndLeave}
              onInitialContextNoteResolved={onInitialContextNoteResolved}
              autoFilterOnViewSelect={showPatientSideSlot}
              initialNewNoteContextType={newNoteSeedContext?.contextType}
              initialNewNoteContextId={newNoteSeedContext?.contextId}
              claimContextNotesReadOnly={claimContextNotesReadOnly}
            />
          </div>
        )
      ) : isEditingNote && editingNote ? (
        <NoteEditor
          key="note-editor-edit"
          contextId={originContextId ?? undefined}
          contactId={originContactId ?? undefined}
          contextType={originContextType ?? NoteContextType.PATIENT}
          contextName={originContextName ?? undefined}
          patientId={selectedPatient?.patient_id}
          openContextNotesByDefault={openContextNotesInEditor}
          patientName={selectedPatientName || 'Unknown Patient'}
          backButtonLabel={backButtonLabel}
          onBack={handleBackFromEdit}
          onSave={handleSaveNote}
          note={editingNote}
          onDelete={handleDeleteNoteFromEditor}
          onEditNote={handleEditNote}
          onToggleSidebar={toggleSidebar}
          onViewTranscription={handleViewTranscription}
          onHideTranscription={handleHideTranscription}
          showTranscription={showTranscribedAudio}
          onTranscriptSegmentsChange={handleTranscriptSegmentsChange}
          onNavigationGuardChange={onNavigationGuardChange}
          onRegisterSaveAndLeave={onRegisterSaveAndLeave}
          autoFilterOnViewSelect={showPatientSideSlot}
          claimContextNotesReadOnly={claimContextNotesReadOnly}
        />
      ) : isViewingNote && viewingNote ? (
        <NoteEditor
          key="note-editor-view"
          contextId={originContextId ?? undefined}
          contactId={originContactId ?? undefined}
          contextType={originContextType ?? NoteContextType.PATIENT}
          contextName={originContextName ?? undefined}
          patientId={selectedPatient?.patient_id}
          openContextNotesByDefault={
            openContextNotesInEditor || (!originContextId && !originContactId)
          }
          patientName={selectedPatientName || 'Unknown Patient'}
          backButtonLabel={backButtonLabel}
          onBack={handleBackFromView}
          onSave={handleSaveNote}
          note={viewingNote}
          mode="view"
          onViewNote={handleViewNote}
          onEditNote={handleEditNote}
          onToggleSidebar={toggleSidebar}
          showTranscription={false}
          onNavigationGuardChange={onNavigationGuardChange}
          onRegisterSaveAndLeave={onRegisterSaveAndLeave}
          autoFilterOnViewSelect={showPatientSideSlot}
          claimContextNotesReadOnly={claimContextNotesReadOnly}
        />
      ) : notesError ? (
        <div className="d-flex flex-column gap-2 p-2">
          <div className="alert alert-danger mb-0" role="alert">
            Error loading notes: {notesError}
          </div>
          {hideNotesTable ? (
            <Button
              type="button"
              size="md"
              className="button-secondary align-self-start"
              onClick={(): void => {
                requestOverlayClose()
              }}
            >
              Close
            </Button>
          ) : null}
        </div>
      ) : hideNotesTable ? null : !showPatientSideSlot &&
        notesLoading ? null : (
        <NotesMainContent
          selectedNotes={selectedPatientNotes}
          patientName={selectedPatientName || 'Select a patient'}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={toggleSidebar}
          onAddNote={handleAddNote}
          addNoteDisabled={claimContextNotesReadOnly}
          onViewNote={handleViewNote}
          onEditNote={claimContextNotesReadOnly ? undefined : handleEditNote}
          onDeleteNote={handleDeleteNote}
          onDownloadNote={handleDownloadNote}
          isLoading={notesLoading}
          searchQuery={noteSearchQuery}
          onSearchChange={handleNoteSearchChange}
          filters={noteFilters}
          onFilterChange={handleNoteFilterChange}
          onResetFilters={handleNoteResetFilters}
          presetContextType={originContextType ?? undefined}
          hideTop={showPatientSideSlot}
          topOverlayBack={
            overlayListCloseInTableHeader
              ? {
                  onClick: requestOverlayClose,
                  label: overlayListCloseLabel,
                }
              : undefined
          }
          onSelectPatient={onSelectPatient}
        />
      )}
    </div>
  )
}

export default NotesFeatureMainColumn
